import { useEffect, useState } from "react";
import { FormattedMessage } from "react-intl";
import { fetchMembers, fetchMembersFromIds } from "../../Api/member";
import {
  chatChannelsAdd,
  chatChannelsInformations,
  membersInformations_members,
} from "../../Api/types";
import { api_postValueOrErrorMessage } from "../../Api/util";
import useFilters from "../../Hook/filters";
import { displayPopin } from "../../Hook/popins";
import useUser, { USER } from "../../Hook/user";
import Button from "../Input/Button";
import { MemberCardSkeleton, MemberRowSkeleton } from "../Skeleton";
import MemberItem, { PodiumCard, RankRow } from "./MemberItem";

function MemberList({
  additionalInputs,
  endpoint = "top",
  common,
  noGlobalFilters,
  progress,
  chat,
  blocked,
  alwaysLoadMore,
  pinSelf
}: {
  additionalInputs?: any;
  noGlobalFilters?: boolean;
  common?: boolean;
  chat?: any;
  progress?: boolean;
  /** 35a — the top list pins your own row at the foot of what's loaded. */
  pinSelf?: boolean;
  endpoint?:
    | "blocked"
    | "fans"
    | "followers"
    | "followed"
    | "highlighted"
    | "profile_visitors"
    | "relations"
    | "search"
    | "top"
    | "top_flames_day"
    | "likes";
  blocked?: boolean;
  alwaysLoadMore?: boolean;
}) {
  const [members, setMembers] = useState<membersInformations_members[]>([]);
  const [page, setPage] = useState<number>(1);
  const [hasMore, setHasMore] = useState<boolean>(false);
  const [isLoading, setLoading] = useState(true);
  const user = useUser();
  const filters = useFilters();

  useEffect(() => {
    //helps calling async API with await syntax instead of then
    (async () => {
      setLoading(true);
      setMembers([]);
      let members = await fetchMembers(endpoint, {
        offset: 0,
        limit: 8,
        ...(noGlobalFilters ? {} : filters.filters),
        ...additionalInputs,
      });
      console.log(members);
      setHasMore(members.length === 8)
      if(alwaysLoadMore)
        members = members.filter(e => e.time_online !== undefined);
      setMembers(
        members
      );
      setLoading(false);
    })();
  }, [filters, additionalInputs]);
  const reload = async () => {
    setLoading(true);
    let members = await fetchMembers(endpoint, {
      offset: 0,
      limit: 8,
      type: "classic",
      ...(noGlobalFilters ? {} : filters.filters),
      ...additionalInputs,
    });
    setHasMore(members.length === 8)
    if(alwaysLoadMore)
      members = members.filter(e => e.time_online !== undefined);
    setMembers(
      members
    );
    setLoading(false);
  };
  const loadMore = async (forcePage?: number) => {
    setLoading(true);
    console.log(forcePage, page);
    let newM = (await fetchMembers(endpoint, {
      offset: (forcePage ?? page) * 8,
      limit: 8,
      ...(noGlobalFilters ? {} : filters.filters),
      ...additionalInputs,
    }))
    let has8Members = newM.length === 8;
    setHasMore(has8Members);
    if(alwaysLoadMore){
      newM = newM.filter(e => e.time_online !== undefined);
      newM = newM.filter(e => !members.find(a => a.id === e.id));
      if(has8Members && newM.length === 0){
        loadMore(forcePage ? forcePage + 1 : page + 1);
        return;
      }
    }
    setPage((p) => (forcePage ?? p) + 1);
    setMembers([
      ...members,
      ...newM,
    ]);
    setLoading(false);
  };
  // 34c — the two top lists take the podium instead of the band grid.
  const isTop = endpoint === "top" || endpoint === "top_flames_day";

  // 35a — your own row, pinned at the foot of the visible list. Only when the
  // API serves a rank and your row hasn't already been loaded into the standing.
  const me = user?.informations as any;
  const selfRank =
    pinSelf && me && me.type !== "guest" && typeof me.rank === "number" && me.rank > 0 &&
    !members.some((member) => member.id === me.id)
      ? me.rank
      : null;

  const openChat = (member: membersInformations_members) => () => {
    if (user?.informations?.type === "guest") return displayPopin()("login");
    let err = USER?.informations?.restrictions?.find((e) => e.type === "chat_access");
    if (err) {
      return displayPopin()("chaterror");
    }
    api_postValueOrErrorMessage<chatChannelsAdd>("chat/channels/add", {
      members: [member.id],
    }).then((channel) => {
      if (!channel.error_message)
        api_postValueOrErrorMessage<chatChannelsInformations>(
          "chat/channels/informations",
          { channels: [channel.channel] },
        ).then(async (chan) => {
          if (!chan.error_message) {
            let currentChan = chan.channels[0];
            let otherMember = currentChan.members.find(
              (e) => e !== user?.informations?.id,
            );
            const other = await fetchMembersFromIds([otherMember as string]);
            currentChan.otherMember = other[0];
            chat(currentChan);
          }
        });
    });
  };

  if (isTop)
    return (
      <div className="w-full max-w-2xl mx-auto">
        {!isLoading && members.length === 0 && (
          <FormattedMessage id="memberlist__no_result" />
        )}
        {members.length > 0 && (
          <div className="grid grid-cols-3 gap-3 items-end pb-4">
            {members.slice(0, 3).map((member, i) => (
              <PodiumCard
                key={member.id}
                member={member}
                rank={i + 1}
                progress={progress}
                // Winner centre, second left, third right — explicit cells, so a
                // list shorter than three keeps the winner on the middle step.
                className={
                  i === 0 ? "col-start-2 row-start-1" : i === 1 ? "col-start-1 row-start-1" : "col-start-3 row-start-1"
                }
              />
            ))}
          </div>
        )}
        {(members.length > 3 || (selfRank !== null && members.length > 0)) && (
          <div className="divide-y divide-tint-neutral border-y border-tint-neutral">
            {members.slice(3).map((member, i) => (
              <RankRow key={member.id} member={member} rank={i + 4} progress={progress} />
            ))}
            {/* Evolution rides the pinned row when served, whichever board it's on. */}
            {selfRank !== null && <RankRow member={me} rank={selfRank} progress />}
          </div>
        )}
        {isLoading && <MemberRowSkeleton count={5} />}
        {!isLoading && hasMore && (
          <div className="mt-8 flex justify-center">
            <Button name="load_more" color="primary" onClick={() => loadMore()} />
          </div>
        )}
      </div>
    );

  return (
    <div>
      {!isLoading && members.length === 0 && (
        <FormattedMessage id="memberlist__no_result" />
      )}
      <div
        className={
          chat
            ? // 34b — the chat list is lines between hairlines, not cards.
              "w-full flex flex-col divide-y divide-tint-neutral border-y border-tint-neutral"
            : // 34a — the band grid: the theme owns the column count and the card
              // only fills its cell, the same feed grid the snaps pack into.
              "w-full max-w-content grid grid-cols-feed tablet:grid-cols-feed-tablet desktop:grid-cols-feed-desktop justify-center m-auto gap-grid-gap desktop:gap-x-grid-gap-column desktop:gap-y-grid-gap-row my-0"
        }
      >
        {members.map((member, i) => (
          <MemberItem
            reload={reload}
            blocked={blocked}
            common={common}
            first={i === 0}
            key={member.id}
            chat={chat}
            member={member}
            onClick={chat ? openChat(member) : null}
          />
        ))}
        {isLoading && (chat ? <MemberRowSkeleton /> : <MemberCardSkeleton />)}
      </div>
      {!isLoading && (hasMore) && (
        <div className="mt-8 flex justify-center">
          <Button name="load_more" color="primary" onClick={() => loadMore()} />
        </div>
      )}
    </div>
  );
}

export default MemberList;
